import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import { OPEN_CASE_STATUSES, type CaseEvent } from '@core/domain/model';
import { timingOf, timingText, type TaskTiming } from '@core/ui/TaskTiming';
import { momentOf } from './grouping';
import type { TaskCaseOption } from './types';

/**
 * Карточка задачи — «Структура» §6.2.
 *
 * Задача заводится в спешке, одним названием и сроком; всё остальное дописывают потом.
 * Поэтому карточка — в первую очередь место, где задачу **дополняют**: описанием, делом,
 * которое вспомнилось позже, сдвинутым сроком.
 */
export interface TaskDetailState {
  isLoading: boolean;
  notFound: boolean;
  title: string;
  description: string;
  deadlineAt: number | null;
  /** «Просрочено — до 28 августа» либо «Сегодня, 15:00». */
  timing: string;
  timingAccent: TaskTiming;
  isCompleted: boolean;
  caseId: string | null;
  caseTitle: string | null;
  /**
   * Клиент дела, к которому привязана задача.
   *
   * Своего клиента у задачи нет: она относится к делу, а дело — к клиенту. Строка
   * нужна затем, чтобы позвонить, не разыскивая клиента через дело.
   */
  clientId: string | null;
  clientName: string | null;
  /** Дела, к которым можно привязать задачу; привязки может и не быть. */
  cases: TaskCaseOption[];
  /**
   * Задача удалена и ждёт «Отменить» ([D-12](docs/DECISIONS.md)). Карточка остаётся
   * на экране: уходить с неё раньше, чем юрист успел передумать, — значит отнять отмену.
   */
  isDeleted: boolean;
}

export function useTaskDetail(taskId: string, db: Repositories, now: () => number = Date.now) {
  const [task, setTask] = useState<CaseEvent | null>(null);
  const [cases, setCases] = useState<TaskCaseOption[]>([]);
  const [caseTitle, setCaseTitle] = useState<string | null>(null);
  const [client, setClient] = useState<{ id: string; name: string } | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isDeleted, setDeleted] = useState(false);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const [found, allCases, allClients] = await Promise.all([
        db.events.find(taskId),
        db.cases.list({}),
        db.clients.list(),
      ]);
      if (dropped) return;

      if (!found) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setTask(found);

      const owner = allCases.find((item) => item.id === found.caseId) ?? null;
      const person = allClients.find((item) => item.id === owner?.clientId) ?? null;

      setCaseTitle(owner?.title ?? null);
      setClient(person ? { id: person.id, name: person.fullName } : null);
      setCases(
        allCases
          .filter((item) => OPEN_CASE_STATUSES.includes(item.status))
          .map((item) => ({ id: item.id, title: item.title })),
      );
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, taskId, version]);

  const state: TaskDetailState = useMemo(() => {
    const moment = task ? momentOf(task) : null;
    const accent = task ? timingOf(momentOf(task), task.isCompleted, now()) : 'later';

    return {
      isLoading,
      notFound,
      title: task?.title ?? '',
      description: task?.description ?? '',
      deadlineAt: moment,
      timing: moment === null ? '' : timingText(moment, accent),
      timingAccent: accent,
      isCompleted: task?.isCompleted ?? false,
      caseId: task?.caseId ?? null,
      caseTitle,
      clientId: client?.id ?? null,
      clientName: client?.name ?? null,
      cases,
      isDeleted,
    };
  }, [task, caseTitle, client, cases, isLoading, notFound, isDeleted, now]);

  /** Правка пишется сразу: карточка не спрашивает «сохранить?», её нечего терять. */
  const edit = useCallback(
    async (change: (current: CaseEvent) => CaseEvent) => {
      if (!task) return;

      await db.events.update(change(task));
      refresh();
    },
    [db, task, refresh],
  );

  const actions = useMemo(
    () => ({
      setTitle: (title: string) =>
        title.trim().length > 0 ? void edit((current) => ({ ...current, title })) : undefined,

      setDescription: (description: string) =>
        void edit((current) => ({ ...current, description: description.trim() || null })),

      /** Срок задачи — это её дедлайн; `startAt` держим на нём же, чтобы календарь совпадал. */
      setDeadline: (deadline: number) =>
        void edit((current) => ({ ...current, startAt: deadline, deadlineAt: deadline })),

      /** Дело привязывается задним числом и отвязывается: привязки может и не быть. */
      setCase: (caseId: string | null) => void edit((current) => ({ ...current, caseId })),

      async setCompleted(isCompleted: boolean) {
        if (!task) return;

        await db.events.setCompleted(task.id, isCompleted);
        refresh();
      },

      async remove() {
        if (!task) return;

        await db.events.remove(task.id);
        setDeleted(true);
      },

      async undoDelete() {
        if (!task) return;

        await db.events.restore(task.id);
        setDeleted(false);
        refresh();
      },

      deletionShown: () => setDeleted(false),
      refresh,
    }),
    [db, task, edit, refresh],
  );

  return { state, actions };
}

export type TaskDetailActions = ReturnType<typeof useTaskDetail>['actions'];
